import React, { useState, useEffect } from 'react';
import { X, Clock, Calendar, PlayCircle, Brain, BookOpen, CheckSquare, FileText, Shield, Upload, PenTool, Loader2 } from 'lucide-react';
import api from '../utils/api';

const TaskPreviewModal = ({ task, isOpen, onClose, onStart }) => {
    const [sections, setSections] = useState([]);
    const [isLoadingSections, setIsLoadingSections] = useState(false);

    useEffect(() => {
        if (isOpen && task && (task.task_type === 'combined' || task.is_combined)) {
            fetchSections();
        } else {
            setSections([]);
        }
    }, [task, isOpen]);

    const fetchSections = async () => {
        setIsLoadingSections(true);
        try {
            // Check if task already has sections (e.g. from detail payload)
            if (task.sections && task.sections.length > 0) {
                setSections(task.sections);
            } else {
                const targetTaskId = task.task_id || task.id;
                const response = await api.get(`/tasks/${targetTaskId}/sections`);
                if (response.data.success) {
                    setSections(response.data.sections);
                }
            }
        } catch (error) {
            console.error("Failed to fetch task sections:", error);
        } finally {
            setIsLoadingSections(false);
        }
    };

    if (!isOpen || !task) return null;

    const getTaskIcon = () => {
        switch (task.task_type) {
            case 'quiz': return Brain;
            case 'training': return BookOpen;
            case 'checklist': return CheckSquare;
            case 'combined': return FileText; // Or a specific combined icon
            default: return FileText;
        }
    };

    const getTaskColor = () => {
        switch (task.task_type) {
            case 'quiz': return 'bg-purple-100 text-purple-600';
            case 'training': return 'bg-blue-100 text-blue-600';
            case 'checklist': return 'bg-green-100 text-green-600';
            case 'combined': return 'bg-indigo-100 text-indigo-600';
            default: return 'bg-primary/10 text-primary';
        }
    };

    const getTodoList = () => {
        switch (task.task_type) {
            case 'quiz':
                return [
                    "Questions are hidden until you start",
                    `Answer ${task.content?.questions?.length || 'all'} questions`,
                    task.quiz_grading_type === 'auto'
                        ? `Score at least ${task.passing_score || 80}% to pass`
                        : "Submit written answers for manager review"
                ];
            case 'training':
                return [
                    "Read through the training content",
                    "Review attached resources",
                    task.estimated_duration ? `Spend approximately ${task.estimated_duration} minutes` : "Complete the module at your own pace"
                ];
            case 'checklist':
                return [
                    "Review all checklist items",
                    "Mark items as complete as you go",
                    "Submit once all required items are checked"
                ];
            default:
                return [
                    "Read the task instructions carefully",
                    "Complete the required actions",
                    task.requires_proof ? "Upload proof of completion" : "Mark as complete when done"
                ];
        }
    };

    const Icon = getTaskIcon();
    const iconColor = getTaskColor();
    const todoList = getTodoList();

    const formatDate = (dateString) => {
        if (!dateString) return 'No due date';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-0 md:p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200 safe-area-inset">
            <div className="bg-white rounded-none md:rounded-2xl w-full h-full md:h-auto md:max-w-lg shadow-xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col md:block">
                {/* Header */}
                <div className="relative p-4 pb-0 md:p-6 md:pb-0 safe-area-top">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>

                    <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-xl ${iconColor}`}>
                            <Icon className="w-8 h-8" />
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                    {task.task_type.charAt(0).toUpperCase() + task.task_type.slice(1)}
                                </span>
                                {task.is_compliance_required && (
                                    <span className="flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                                        <Shield className="w-3 h-3" />
                                        Compliance
                                    </span>
                                )}
                                {task.status === 'retake_requested' && (
                                    <span className="flex items-center gap-1 text-xs font-medium text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                                        <Clock className="w-3 h-3" />
                                        Retake Requested
                                    </span>
                                )}
                            </div>
                            <h2 className="text-xl font-bold text-gray-900 leading-tight">
                                {task.title}
                            </h2>
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div className="p-4 md:p-6 space-y-6 flex-1 overflow-y-auto">
                    {/* Meta Info */}
                    <div className="flex items-center gap-6 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span>Due: {formatDate(task.due_date)}</span>
                        </div>
                        {task.estimated_duration && (
                            <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-gray-400" />
                                <span>~{task.estimated_duration} mins</span>
                            </div>
                        )}
                        {/* Show count of sections if combined */}
                        {(task.task_type === 'combined' || task.is_combined) && (
                            <div className="flex items-center gap-2">
                                <CheckSquare className="w-4 h-4 text-gray-400" />
                                <span>{sections.length || '?'} Sections</span>
                            </div>
                        )}
                    </div>

                    {/* Content Area: Sections List OR Todo List */}
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                        <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide">
                            {(task.task_type === 'combined' || task.is_combined) ? "Sections to Complete" : "What You'll Do"}
                        </h3>

                        {(task.task_type === 'combined' || task.is_combined) ? (
                            isLoadingSections ? (
                                <div className="flex items-center justify-center py-4">
                                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                                </div>
                            ) : sections.length > 0 ? (
                                <ul className="space-y-2">
                                    {sections.map((section, idx) => {
                                        const SectionIcon = {
                                            quiz: Brain,
                                            training: BookOpen,
                                            checklist: CheckSquare,
                                            content: FileText,
                                            proof_upload: Upload,
                                            signature: PenTool
                                        }[section.section_type] || FileText;

                                        // Extract metadata
                                        let metaInfo = [];
                                        if (section.estimated_duration) metaInfo.push(`${section.estimated_duration}m`);

                                        try {
                                            const parsed = typeof section.content === 'string' ? JSON.parse(section.content) : section.content;
                                            if (section.section_type === 'quiz') {
                                                metaInfo.push(`${parsed.questions?.length || 0} Qs`);
                                                if (section.passing_score) metaInfo.push(`${section.passing_score}% Pass`);
                                            } else if (section.section_type === 'training') {
                                                // metaInfo.push("Read");
                                            } else if (section.section_type === 'checklist') {
                                                metaInfo.push(`${parsed.items?.length || 0} Items`);
                                            }
                                        } catch (e) { }

                                        return (
                                            <li key={section.id || idx} className="flex items-center gap-3 p-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700">
                                                <div className="flex-shrink-0 w-6 h-6 bg-gray-100 rounded flex items-center justify-center">
                                                    <span className="text-xs font-medium text-gray-500">{idx + 1}</span>
                                                </div>
                                                <SectionIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between">
                                                        <span className="truncate font-medium">{section.title}</span>
                                                        {section.is_required && (
                                                            <span className="text-[10px] uppercase text-red-500 font-bold bg-red-50 px-1 rounded ml-2">Req</span>
                                                        )}
                                                    </div>
                                                    {metaInfo.length > 0 && (
                                                        <div className="text-xs text-gray-400 flex gap-1.5 mt-0.5">
                                                            {metaInfo.map((info, i) => (
                                                                <span key={i}>{info}{i < metaInfo.length - 1 ? ' • ' : ''}</span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            ) : (
                                <p className="text-sm text-gray-500 italic">No sections found.</p>
                            )
                        ) : (
                            <ul className="space-y-3">
                                {todoList.map((item, idx) => (
                                    <li key={idx} className="flex items-start gap-3 text-sm text-gray-600">
                                        <div className="mt-1 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="p-4 md:p-6 md:pt-0 safe-area-bottom">
                    <button
                        onClick={onStart}
                        disabled={task.status === 'retake_requested'}
                        className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold transition-all shadow-lg 
                            ${task.status === 'retake_requested'
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed shadow-none'
                                : 'bg-primary hover:bg-primary-hover text-white shadow-primary/20 hover:shadow-primary/30'}`}
                    >
                        {task.status === 'retake_requested' ? (
                            <>
                                <Clock className="w-5 h-5" />
                                Retake Pending
                            </>
                        ) : (
                            <>
                                <PlayCircle className="w-5 h-5" />
                                Start Task
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TaskPreviewModal;
